/**
 * Forge API client — catalog + connection routes.
 * Auth is injected; no hard dependency on any app's auth store.
 */

import { dumpModelsQuery } from "./catalog.js";
import type { InferenceCatalogResponse } from "./types.js";

export class GpuClientError extends Error {
  constructor(
    message: string,
    public status?: number
  ) {
    super(message);
    this.name = "GpuClientError";
  }
}

export type GetAccessToken = () => string | null | undefined;

export interface ForgeClientOptions {
  forgeApiUrl: string;
  getAccessToken?: GetAccessToken;
  /** AccessHub app id — dump ∩ filter. */
  appId?: string;
  /** Active tool_id so dump clips to that tool's model type. */
  toolId?: string;
  /** ProxyPro origin that holds main_dump. */
  dumpCatalogUrl?: string;
}

function humanizeError(status: number | undefined, body: string, baseUrl?: string): string {
  const raw = (body || "").trim();
  const lower = raw.toLowerCase();
  const looksHtml =
    lower.startsWith("<!doctype") ||
    lower.startsWith("<html") ||
    lower.includes("cloudflare tunnel error") ||
    lower.includes("error code 1033");

  if (status === 503 || status === 530 || status === 502) {
    return (
      `Forge origin is offline (${status}). Start forge-backend + tunnel` +
      (baseUrl ? ` · ${baseUrl}` : "")
    );
  }
  if (looksHtml) {
    return `Forge origin tunnel is down (got HTML ${status ?? "error"} instead of API JSON).`;
  }
  if (raw) {
    try {
      const j = JSON.parse(raw) as {
        error?: string | { message?: string; detail?: string };
        message?: string;
        detail?: string;
      };
      const err = j.error;
      if (typeof err === "string" && err.trim()) return err.slice(0, 320);
      if (err && typeof err === "object") {
        const nested = err.message || err.detail;
        if (nested) return String(nested).slice(0, 320);
      }
      return j.message || j.detail || raw.slice(0, 280);
    } catch {
      return raw.length > 280 ? `${raw.slice(0, 280)}…` : raw;
    }
  }
  return `Forge API error ${status ?? "unknown"}`;
}

const SUITE_RELAY = "https://bookforge-relay.forgesuiteui.app";

function apiBases(primary: string): string[] {
  const first = (primary || "").replace(/\/$/, "");
  const out: string[] = [];
  if (first) out.push(first);
  if (!out.includes(SUITE_RELAY)) out.push(SUITE_RELAY);
  return out;
}

export async function forgeFetch<T>(
  opts: ForgeClientOptions,
  path: string,
  init?: RequestInit
): Promise<T> {
  const tok = opts.getAccessToken?.() || null;
  const errors: string[] = [];
  for (const base of apiBases(opts.forgeApiUrl)) {
    const url = `${base}${path}`;
    try {
      const res = await fetch(url, {
        ...init,
        headers: {
          "Content-Type": "application/json",
          ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
          ...(init?.headers || {}),
        },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        if (res.status === 503) {
          try {
            const j = JSON.parse(body) as { phase?: string; code?: string };
            if (j.phase === "waking" || j.code === "waking") {
              return j as T;
            }
          } catch {
            /* fall through */
          }
        }
        errors.push(humanizeError(res.status, body, base));
        if (res.status !== 502 && res.status !== 503 && res.status !== 530) {
          throw new GpuClientError(errors[errors.length - 1], res.status);
        }
        continue;
      }
      return res.json() as Promise<T>;
    } catch (e) {
      if (e instanceof GpuClientError) throw e;
      errors.push(`Cannot reach ${base}`);
    }
  }
  throw new GpuClientError(
    errors[0] ||
      "Cannot reach Forge API. BookForge origin (relay) is the catalog source.",
  );
}

export type CatalogTabQuery = {
  modality?: string;
  force?: boolean;
  vision?: boolean;
  capability?: string;
  tag?: string;
  q?: string;
  types?: string;
  app?: string;
  tool?: string;
  cursor?: string;
  browse?: boolean;
};

function catalogQs(query?: CatalogTabQuery): string {
  const qs = new URLSearchParams({ modality: query?.modality || "all" });
  if (query?.force) qs.set("force", "1");
  if (query?.vision) qs.set("vision", "1");
  if (query?.capability) qs.set("capability", query.capability);
  if (query?.tag) qs.set("tag", query.tag);
  if (query?.q) qs.set("q", query.q);
  if (query?.types) qs.set("types", query.types);
  if (query?.app) qs.set("app", query.app);
  if (query?.tool) qs.set("tool", query.tool);
  if (query?.cursor) qs.set("cursor", query.cursor);
  if (query?.browse) qs.set("browse", "1");
  return qs.toString();
}

function modalityFromTags(tags: string[] | undefined, fallback = "text-generation"): string {
  const t = (tags || []).map((x) => String(x).toLowerCase());
  if (t.some((x) => x === "text-to-speech" || x === "tts")) return "text-to-speech";
  if (t.some((x) => x === "text-to-image" || x === "image-to-image")) return "text-to-image";
  if (t.some((x) => x === "text-to-video" || x === "image-to-video")) return "text-to-video";
  if (t.some((x) => x === "text-generation" || x.startsWith("modality:text-generation"))) {
    return "text-generation";
  }
  if (t.includes("text-input") && t.includes("text-output")) return "text-generation";
  return fallback;
}

function normalizeCatalog(data: InferenceCatalogResponse): InferenceCatalogResponse {
  const models = (data.models || []).map((m) => {
    const tags = m.tags || [];
    const modelId = m.modelId || (m as { slug?: string }).slug || m.skuId || m.name;
    return {
      ...m,
      modelId,
      skuId: m.skuId || modelId,
      name: m.name || modelId,
      modality: m.modality || modalityFromTags(tags),
      provider: m.provider,
      tags,
      offer: m.offer || {
        id: modelId,
        pricePerHour: 0,
        available: true,
      },
    };
  });
  const byModality = { ...(data.byModality || {}) };
  if (!Object.keys(byModality).length) {
    for (const m of models) {
      const k = (m.modality || "other").toLowerCase();
      byModality[k] = (byModality[k] || 0) + 1;
    }
  }
  return {
    ...data,
    configured: data.configured !== false,
    total: data.total ?? models.length,
    byModality,
    models,
  };
}

const DUMP_CATALOG = "https://proxy.vividlex.com";

export async function fetchDumpCatalog(
  opts: ForgeClientOptions,
  tab: string,
  query?: CatalogTabQuery,
): Promise<InferenceCatalogResponse | null> {
  const app = (query?.app || opts.appId || "").trim();
  if (!app) return null;
  const dumpBase = (opts.dumpCatalogUrl || DUMP_CATALOG).replace(/\/$/, "");
  const qs = dumpModelsQuery({
    app,
    tab,
    tool: query?.tool || opts.toolId,
    q: query?.q,
  });
  try {
    const res = await fetch(`${dumpBase}/api/catalog/models?${qs.toString()}`);
    if (!res.ok) return null;
    const j = (await res.json()) as {
      ok?: boolean;
      models?: InferenceCatalogResponse["models"];
      total?: number;
    };
    if (!j || !Array.isArray(j.models)) return null;
    return normalizeCatalog({
      models: j.models,
      total: j.total ?? j.models.length,
      configured: true,
      note: j.models.length
        ? "Main dump (AccessHub)."
        : "Main dump is empty for this filter. Pull today in AccessHub.",
    });
  } catch {
    return null;
  }
}

async function fetchSuiteCatalog(
  opts: ForgeClientOptions,
  tab: string,
  query?: CatalogTabQuery,
): Promise<InferenceCatalogResponse> {
  const dumped = await fetchDumpCatalog(opts, tab, query);
  if (dumped) return dumped;
  const qs = catalogQs(query);
  return normalizeCatalog(await forgeFetch(opts, `/api/suite/catalog?tab=${tab}&${qs}`));
}

export function fetchHfCatalog(
  opts: ForgeClientOptions,
  query?: CatalogTabQuery
): Promise<InferenceCatalogResponse> {
  return fetchSuiteCatalog(opts, "hf", query);
}

export async function fetchProductionCatalog(
  opts: ForgeClientOptions,
  query?: CatalogTabQuery,
): Promise<InferenceCatalogResponse> {
  const dumped = await fetchDumpCatalog(opts, "production", query);
  if (dumped) return dumped;
  return normalizeCatalog({
    models: [],
    total: 0,
    configured: true,
    note: "No production assignments yet. Pick a dump row on a tool in AccessHub.",
  });
}

type HubProviderMapping = {
  provider?: string;
  providerId?: string;
  status?: string;
  task?: string;
  type?: string;
  adapter?: string;
};

type HubMediaRow = {
  id?: string;
  inferenceProviderMapping?: HubProviderMapping[];
};

const HUB_MEDIA_PROVIDERS = ["fal-ai", "replicate", "wavespeed", "novita"] as const;

function liveHubMapping(row: HubMediaRow): HubProviderMapping | null {
  const maps = row.inferenceProviderMapping || [];
  for (const m of maps) {
    const status = String(m.status || "live").toLowerCase();
    if (status !== "live" && status !== "available") continue;
    if (String(m.adapter || "").toLowerCase() === "lora") continue;
    if (String(m.type || "").toLowerCase() === "tag-filter") continue;
    if (!m.provider) continue;
    return m;
  }
  return null;
}

function hubRowToModel(row: HubMediaRow, fallbackTask: string): InferenceModel | null {
  const hubId = String(row.id || "").trim();
  if (!hubId.includes("/")) return null;
  const map = liveHubMapping(row);
  if (!map) return null;
  const provider = String(map.provider).toLowerCase();
  const task = String(map.task || fallbackTask)
    .toLowerCase()
    .replace(/\s+/g, "-");
  const name = hubId.split("/").pop() || hubId;
  const skuId = `${hubId.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}__${provider}`;
  const video = task.includes("video");
  const image = task.includes("image");
  return {
    skuId,
    modelId: hubId,
    chatModelId: `${hubId}:${provider}`,
    name,
    modality: task || fallbackTask,
    provider,
    tags: ["forge", "live", task, `modality:${task}`],
    capabilities: {
      textInput: !task.startsWith("image-to"),
      textOutput: false,
      imageInput: task.includes("image-to") || image,
      imageOutput: image && !video,
      audioInput: false,
      audioOutput: task.includes("speech"),
      videoInput: false,
      videoOutput: video,
      multimodal: false,
    },
    offer: {
      id: skuId,
      pricePerHour: video ? 0.5 : image ? 0.2 : 0.1,
      available: true,
      provider: "hf-inference",
      catalogNote: "Hugging Face Inference Providers",
    },
  };
}

/**
 * Hub live Inference Provider media — used when the suite HF snapshot is
 * chat-only (router /v1/models). Browser CORS is allowed on huggingface.co.
 */
export async function fetchHubProviderModels(
  tasks: string[],
): Promise<InferenceModel[]> {
  const want = tasks
    .map((t) => t.toLowerCase().replace(/\s+/g, "-"))
    .filter((t) =>
      ["text-to-video", "image-to-video", "text-to-image", "text-to-speech"].includes(t),
    );
  if (!want.length) return [];
  const jobs: Promise<InferenceModel[]>[] = [];
  for (const task of want) {
    for (const provider of HUB_MEDIA_PROVIDERS) {
      jobs.push(
        (async () => {
          const qs = new URLSearchParams({
            pipeline_tag: task,
            inference_provider: provider,
            limit: "40",
          });
          qs.append("expand[]", "inferenceProviderMapping");
          const res = await fetch(`https://huggingface.co/api/models?${qs}`, {
            signal: AbortSignal.timeout(20_000),
          });
          if (!res.ok) return [];
          const rows = (await res.json()) as HubMediaRow[];
          if (!Array.isArray(rows)) return [];
          const out: InferenceModel[] = [];
          for (const row of rows) {
            const m = hubRowToModel(row, task);
            if (m) out.push(m);
          }
          return out;
        })().catch(() => [] as InferenceModel[]),
      );
    }
  }
  const chunks = await Promise.all(jobs);
  const seen = new Set<string>();
  const models: InferenceModel[] = [];
  for (const list of chunks) {
    for (const m of list) {
      if (seen.has(m.modelId)) continue;
      seen.add(m.modelId);
      models.push(m);
    }
  }
  return models;
}

export function fetchCloudflareCatalog(
  opts: ForgeClientOptions,
  query?: CatalogTabQuery
): Promise<InferenceCatalogResponse> {
  return fetchSuiteCatalog(opts, "cloudflare", query);
}

export function fetchCivitaiCatalog(
  opts: ForgeClientOptions,
  query?: CatalogTabQuery
): Promise<InferenceCatalogResponse> {
  return fetchSuiteCatalog(opts, "civitai", query);
}

/** Same Cloudflare feed as /api/inference/catalog (legacy path). */
export function fetchInferenceCatalog(
  opts: ForgeClientOptions,
  query?: CatalogTabQuery
): Promise<InferenceCatalogResponse> {
  return fetchCloudflareCatalog(opts, query);
}

export function modelHasImageInput(m: {
  capabilities?: { imageInput?: boolean; multimodal?: boolean };
  tags?: string[];
  inputModalities?: string[];
}): boolean {
  if (m.capabilities?.imageInput || m.capabilities?.multimodal) return true;
  if (m.inputModalities?.some((x) => String(x).toLowerCase() === "image")) return true;
  return (m.tags || []).some((t) =>
    /^(vision|image-input|multimodal|vlm)$/i.test(String(t))
  );
}

export function isCloudflareSkuRow(row: {
  hardwareId?: string;
  offer?: { provider?: string; id?: string };
  tags?: string[];
  provider?: string;
}): boolean {
  return (
    row.hardwareId === "forge-cf" ||
    row.offer?.provider === "cloudflare" ||
    String(row.offer?.id || "").startsWith("cf_inf_") ||
    (row.tags || []).includes("cloudflare") ||
    (row.tags || []).includes("one-bill") ||
    String(row.provider || "").toLowerCase().includes("cloudflare")
  );
}

export const DEFAULT_PAIR_PROMPT = "Reply with the single word ok.";

/**
 * One prompt against the locked (model × engine) pair. Origin skips debit
 * when x-forge-probe=1. This is the reliability check before Connect.
 */
export async function probeCatalogChat(
  opts: ForgeClientOptions,
  body: { model: string; prompt?: string; appId?: string },
): Promise<{ ok: true; text: string } | { ok: false; error: string }> {
  const model = String(body.model || "").trim();
  if (!model) return { ok: false, error: "No model × engine pair selected." };
  const prompt = String(body.prompt || DEFAULT_PAIR_PROMPT).trim().slice(0, 500);
  try {
    const data = await forgeFetch<{
      choices?: Array<{ message?: { content?: string }; text?: string }>;
      error?: { message?: string } | string;
    }>(opts, "/v1/chat/completions", {
      method: "POST",
      headers: {
        "x-forge-probe": "1",
        ...(body.appId ? { "x-forge-app": body.appId } : {}),
      },
      body: JSON.stringify({
        model,
        messages: [{ role: "user", content: prompt || DEFAULT_PAIR_PROMPT }],
        max_tokens: 48,
        stream: false,
      }),
    });
    const nested =
      typeof data.error === "string"
        ? data.error
        : data.error && typeof data.error === "object"
          ? data.error.message
          : "";
    if (nested) return { ok: false, error: nested.slice(0, 320) };
    const text = String(
      data.choices?.[0]?.message?.content || data.choices?.[0]?.text || "",
    ).trim();
    if (!text) return { ok: false, error: "Engine returned an empty reply." };
    return { ok: true, text: text.slice(0, 400) };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}
